import { useState, useCallback } from 'react'
import { useAppDispatch } from '../../common/hooks'
import ReactPixel from 'react-facebook-pixel'
import { getUTMParams } from '../../common/utils'
import { SignupTextInput } from './SignupTextInput'
import { SignupSelectInput } from './SignupSelectInput'
import {
  StyledContainer,
  StyledSubmitContainer,
  StyledSaveButton,
  StyledSpinner,
  StyledError,
  StyledHeader,
  StyledSubtext,
} from './styles/PreSignupForm.style'
import { submitPreSignup } from '../../services/n8nApi'

interface IPreSignupFormInputs {
  firstName: string
  lastName: string
  phone: string
  cnpj: string
  businessType: string
}

const BUSINESS_TYPE_OPTIONS = [
  { value: 'mercado_mercearia', label: 'Mercado / Mercearia' },
  { value: 'padaria', label: 'Padaria' },
  { value: 'loja_conveniencia', label: 'Loja de Conveniência' },
  { value: 'restaurante_deposito', label: 'Restaurante / Bar / Depósito' },
  { value: 'farmacia', label: 'Farmácia' },
  { value: 'representante_comercial', label: 'Representante Comercial' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'outro', label: 'Outro' },
]
const initialValues: IPreSignupFormInputs = {
  firstName: '',
  lastName: '',
  phone: '',
  cnpj: '',
  businessType: '',
}

export const PreSignupForm = ({
  onSubmitSuccess,
}: {
  onSubmitSuccess: (data: IPreSignupFormInputs) => void
}) => {
  const dispatch = useAppDispatch()
  const [formData, setFormData] = useState<IPreSignupFormInputs>(initialValues)
  const [errors, setErrors] = useState<Partial<IPreSignupFormInputs & { general?: string }>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleInputChange = useCallback((name: keyof IPreSignupFormInputs, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }))
  }, [])

  const validate = useCallback((data: IPreSignupFormInputs): Partial<IPreSignupFormInputs> => {
    const newErrors: Partial<IPreSignupFormInputs> = {}

    if (!data.firstName.trim()) newErrors.firstName = 'O nome é obrigatório.'
    if (!data.lastName.trim()) newErrors.lastName = 'O sobrenome é obrigatório.'
    if (!data.businessType) newErrors.businessType = 'Selecione o tipo de negócio.'
    if (data.cnpj.replace(/\D/g, '').length !== 14)
      newErrors.cnpj = 'O CNPJ é obrigatório e deve ter 14 dígitos.'

    if (data.phone.replace(/\D/g, '').length < 10)
      newErrors.phone = 'O telefone é obrigatório e deve ser válido.'

    return newErrors
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      const validationErrors = validate(formData)
      setErrors(validationErrors)

      if (Object.keys(validationErrors).length > 0) {
        return
      }

      setIsSubmitting(true)

      try {
        await submitPreSignup(formData)
        onSubmitSuccess(formData)
      } catch (error: any) {
        const errorMessage = error.message || 'Falha ao processar o cadastro. Tente novamente.'

        setErrors((prev) => ({
          ...prev,
          general: errorMessage,
        }))
      } finally {
        const utmParams = getUTMParams()
        ReactPixel.track('Lead', {
          ...utmParams,
        })
        setIsSubmitting(false)
      }
    },
    [formData, onSubmitSuccess, validate, dispatch]
  )

  return (
    <StyledContainer>
      <StyledHeader>
        <h2>Faça seu pré-cadastro</h2>
        <p>
          Somos uma distribuidora que atende todo estado do RJ. Nosso catálogo conta com mais de
          2000 produtos!
        </p>
      </StyledHeader>
      <hr />
      <StyledSubtext>
        Preencha suas informações e um dos nossos representantes entrará em contato.
      </StyledSubtext>

      {errors.general && (
        <StyledError style={{ marginBottom: '10px' }}>{errors.general}</StyledError>
      )}

      <form onSubmit={handleSubmit}>
        <SignupTextInput
          label="Nome"
          name="firstName"
          value={formData.firstName}
          onChange={handleInputChange}
          error={errors.firstName}
          placeholder="João"
        />

        <SignupTextInput
          label="Sobrenome"
          name="lastName"
          value={formData.lastName}
          onChange={handleInputChange}
          error={errors.lastName}
          placeholder="Silva"
        />

        <SignupTextInput
          label="Telefone"
          name="phone"
          value={formData.phone}
          isPhone={true}
          onChange={handleInputChange}
          error={errors.phone}
          placeholder="(21) 99999-9999"
        />

        <SignupTextInput
          label="CNPJ"
          name="cnpj"
          value={formData.cnpj}
          isOptional={false}
          isCNPJ={true}
          onChange={handleInputChange}
          error={errors.cnpj}
          placeholder="00.000.000/0001-00"
        />

        <SignupSelectInput
          label="Qual seu tipo de negócio?"
          name="businessType"
          value={formData.businessType}
          options={BUSINESS_TYPE_OPTIONS}
          onChange={handleInputChange}
          error={errors.businessType}
        />

        <StyledSubmitContainer>
          <StyledSaveButton disabled={isSubmitting}>
            {isSubmitting ? <StyledSpinner /> : 'Cadastrar'}
          </StyledSaveButton>
        </StyledSubmitContainer>
      </form>
    </StyledContainer>
  )
}
